import { useState } from 'react'

import { Button, EnterChip } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { useEnterToContinue, useSaveStep, useSkipStep } from '../useWizard'
import { WizardActions } from '../WizardShell'
import type { ContactPayload } from '@/types/api'

interface Props {
  onDone: () => void
  initial?: Partial<ContactPayload>
}

export function ContactStep({ onDone, initial }: Props) {
  const save = useSaveStep('contact')
  const skip = useSkipStep()

  const [phone, setPhone] = useState(initial?.phoneNumber ?? '')
  const [linkedin, setLinkedin] = useState(initial?.linkedinUrl ?? '')

  const submit = () => {
    if (save.isPending) return
    save.mutate(
      { phoneNumber: phone.trim() || null, linkedinUrl: linkedin.trim() || null },
      { onSuccess: onDone },
    )
  }

  useEnterToContinue(submit)
  const fieldError = (name: string) => save.error?.fieldErrors[name] ?? null

  return (
    <>
      <div className="space-y-7">
        <TextField
          label="Phone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="+91 98765 43210"
          hint="Start with + and the country code: +1 for US/Canada, +91 for India."
          autoComplete="tel"
          inputMode="tel"
          autoFocus
          error={fieldError('phoneNumber')}
        />

        <TextField
          label="LinkedIn"
          value={linkedin}
          onChange={(event) => setLinkedin(event.target.value)}
          placeholder="https://linkedin.com/in/yourhandle"
          hint="Asked on basically every Workday / Greenhouse application."
          autoComplete="url"
          inputMode="url"
          autoCapitalize="none"
          spellCheck={false}
          error={fieldError('linkedinUrl')}
        />
      </div>

      <WizardActions>
        <Button
          variant="ghost"
          size="lg"
          className="shrink-0 sm:order-first"
          onClick={() => skip.mutate({ step: 'contact' }, { onSuccess: onDone })}
          loading={skip.isPending}
        >
          Skip for now
        </Button>
        <Button size="lg" className="flex-1 sm:flex-none" onClick={submit} loading={save.isPending}>
          Continue
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}
